// External Libraries
import { useRoutes, Navigate } from 'react-router-dom'
import styled from 'styled-components'
import { Progress, Spin, Typography } from 'antd'
import { CloseOutlined } from '@ant-design/icons'

// Styles
import './app.less'

// Redux & Hooks
import { useAppSelector } from './redux/hooks'
import useUploadProgress from './custom-hooks/useUploadProgress'

// Auth & Utils
import { LoginAuth } from '@/auths/'
import { lazyWithRetry } from './utils/lazyWithRetry'

// Components
import { IconFont } from './components'
import { FullscreenProvider } from './contexts/FullscreenContext'

// Layouts
import { AdminLayout } from './layouts/admin-layout'
import { FishingLayout } from './layouts//fishing-layout'
import { GatewayLayout } from './layouts/gateway-layout'
import { EmailLayout } from './layouts/email-layout'
import { WafLayout } from './layouts/waf-layout'
import { ThreatProtectionLayout } from './layouts/threatprotection'
import { DataProtectionLayout } from './layouts/dataprotection-layout'
import { EndpointSecurityLayout } from './layouts/endpoint-security-layout'
import { ContainerSecurityLayout } from './layouts/container-security-layout'
import { ResourceLayout } from './layouts/resource-layout'
import { LogLayout } from './layouts/log-layout'
import { NormalLayout } from './layouts/normal-layout'
import { ReportTaskLayout } from './layouts/report-task-layout'

// Basic Pages
import Login from './pages/login'
import Maintenance from './pages/maintenance'
import Outdated from './pages/outdated'
import CommonProps from './pages/modification'
import { ClientDownload } from './pages/client/download'
import { ClientList } from './pages/client/list'
import ReportTask from './pages/evaluation/report-task/index'
import GeneralReport from './pages/evaluation/general-report/index'
import { SecurityDomain } from './pages/client/security-domain'
import ProxyList from './pages/waf/proxy/'

const { Text } = Typography

// 攻击编排
const Program = lazyWithRetry(() => import('./pages/program'))
const ProgramList = lazyWithRetry(() => import('./pages/program/list'))
const ScriptConfiguration = lazyWithRetry(() => import('./pages/program/script-configuration'))
const ScriptDetail = lazyWithRetry(() => import('./pages/program/script-detail'))
const CreateScript = lazyWithRetry(() => import('./pages/program/create-script'))
const Temporary = lazyWithRetry(() => import('./pages/program/temporary'))

// 钓鱼邮件
// 创建任务
const Create = lazyWithRetry(() => import('./pages/phishing/create'))

// 威胁情报
// 创建任务
const CreateTask = lazyWithRetry(() => import('./pages/gateway/create-task'))

// 邮件网关
// 创建任务
const EmailCreateTask = lazyWithRetry(() => import('./pages/email/create-task'))

// Waf
// 创建任务
const CreateWaf = lazyWithRetry(() => import('./pages/waf/create-task'))

// 流量安全
// 创建任务
const CreateProtection = lazyWithRetry(() => import('./pages/threat-protection/create-task'))

// 终端安全
// 创建任务
const EndpointSecurityCreate = lazyWithRetry(() => import('./pages/endpoint-security/create-task'))

// 容器安全
// 创建任务
const ContainerSecurityCreate = lazyWithRetry(
  () => import('./pages/container-security/create-task'),
)

// 数据保护
// 创建任务
const DataProtectionCreate = lazyWithRetry(() => import('./pages/data-protection/create-task'))

// 仪表盘
const Dashboard = lazyWithRetry(() => import('./pages/dashboard'))
const Attck = lazyWithRetry(() => import('./pages/attck'))
const Loading = lazyWithRetry(() => import('./pages/dashboard-old/loading'))

// 配置中心
const Resource = lazyWithRetry(() => import('./pages/account-center/site-resource'))
const Collect = lazyWithRetry(() => import('./pages/account-center/site-collect/index'))
const Asset = lazyWithRetry(() => import('./pages/account-center/asset-certification/index'))
const VerifyHost = lazyWithRetry(() => import('./pages/asset-management/verify-host'))
const VerifyMailbox = lazyWithRetry(() => import('./pages/asset-management/verify-mailbox'))
const AnalyticalOverview = lazyWithRetry(
  () => import('./pages/safety-equipment/analytical-overview'),
)
const LinkManagement = lazyWithRetry(() => import('./pages/safety-equipment/link-management'))
const BrandManagement = lazyWithRetry(() => import('./pages/safety-equipment/brand-management'))
const TypeManagement = lazyWithRetry(() => import('./pages/safety-equipment/type-management'))
const AccessManagement = lazyWithRetry(() => import('./pages/safety-equipment/access-management'))
const TemplateCenter = lazyWithRetry(() => import('./pages/useCase-knowledge-base/template-center'))
const UseCaseCenter = lazyWithRetry(() => import('./pages/useCase-knowledge-base/useCase-center'))
const LogManagement = lazyWithRetry(() => import('./pages/log-management'))

// 系统管理
const OpenAPI = lazyWithRetry(() => import('./pages/account-center/open-api'))
const EmailServer = lazyWithRetry(() => import('./pages/account-center/email-server'))
const EmailManage = lazyWithRetry(() => import('./pages/account-center/email-manage'))
const OperateLog = lazyWithRetry(() => import('./pages/account-center/operate-log'))
const DataOutsource = lazyWithRetry(() => import('./pages/account-center/data-outsource'))
const LinkageManagement = lazyWithRetry(() => import('./pages/account-center/linkage-management'))
const TaskManagement = lazyWithRetry(() => import('./pages/account-center/task-management'))
const AttackManagement = lazyWithRetry(() => import('./pages/account-center/attck-manage'))
const AuthorizeManage = lazyWithRetry(() => import('./pages/account-center/authorize-manage'))
const RuleUpgrade = lazyWithRetry(() => import('./pages/account-center/rule-upgrade'))
const SystemUpgrade = lazyWithRetry(() => import('./pages/account-center/system-upgrade'))
const ServiceMonitor = lazyWithRetry(() => import('./pages/account-center/service-monitor'))

// 日志管理
const SourceManage = lazyWithRetry(() => import('./pages/account-center/journaling'))
const ParseRule = lazyWithRetry(() => import('./pages/account-center/parse'))
const Filtration = lazyWithRetry(() => import('./pages/account-center/filtration'))
const LogQuery = lazyWithRetry(() => import('./pages/account-center/log-query'))
const LogGather = lazyWithRetry(() => import('./pages/account-center/client'))
const About = lazyWithRetry(() => import('./pages/account-center/about'))

// 我的  账号中心
const Account = lazyWithRetry(() => import('./pages/account-center'))
const BasicInfo = lazyWithRetry(() => import('./pages/account-center/basic-info'))
const Security = lazyWithRetry(() => import('./pages/account-center/security'))
// 添加
const AddJournaling = lazyWithRetry(() => import('./pages/account-center/add-journaling'))

export const App = () => {
  const upgradeStatus = useAppSelector(state => state.upload.upgradeStatus)
  const globalLoading = useAppSelector(state => state.upload.globalLoading)
  const { progress, status, open, filename, remainingTime, closeCard } = useUploadProgress()
  const routes = useRoutes([
    {
      path: '/*',
      children: [
        {
          path: '*',
          children: [
            {
              path: 'validation/*',
              element: (
                <LoginAuth>
                  <AdminLayout />
                </LoginAuth>
              ),
              children: [
                {
                  path: 'awareness/phishing/*',
                  element: <FishingLayout />,
                  children: [
                    {
                      path: '',
                      element: <Create />,
                    },
                    {
                      path: '*',
                      element: <Navigate to={'create'} />,
                    },
                  ],
                },
                {
                  path: 'network/webgateway/*',
                  element: <GatewayLayout />,
                  children: [
                    {
                      path: '',
                      element: <CreateTask />,
                    },
                    {
                      path: '*',
                      element: <Navigate to={'create'} />,
                    },
                  ],
                },
                {
                  path: 'network/emailgateway/*',
                  element: <EmailLayout />,
                  children: [
                    {
                      path: '',
                      element: <EmailCreateTask />,
                    },
                    {
                      path: '*',
                      element: <Navigate to={'create'} />,
                    },
                  ],
                },
                {
                  path: 'network/waf/*',
                  element: <WafLayout />,
                  children: [
                    {
                      path: '',
                      element: <CreateWaf />,
                    },
                    {
                      path: '*',
                      element: <Navigate to={'create'} />,
                    },
                  ],
                },
                {
                  path: 'network/threatprotection/*',
                  element: <ThreatProtectionLayout />,
                  children: [
                    {
                      path: '',
                      element: <CreateProtection />,
                    },
                    {
                      path: '*',
                      element: <Navigate to={'create'} />,
                    },
                  ],
                },
                {
                  path: 'network/endpointsecurity/*',
                  element: <EndpointSecurityLayout />,
                  children: [
                    {
                      path: '',
                      element: <EndpointSecurityCreate />,
                    },
                    {
                      path: '*',
                      element: <Navigate to={'create'} />,
                    },
                  ],
                },
                {
                  path: 'cloud/containersecurity/*',
                  element: <ContainerSecurityLayout />,
                  children: [
                    {
                      path: '',
                      element: <ContainerSecurityCreate />,
                    },
                    {
                      path: '*',
                      element: <Navigate to={'create'} />,
                    },
                  ],
                },
                {
                  path: 'data/dataprotection/*',
                  element: <DataProtectionLayout />,
                  children: [
                    {
                      path: '',
                      element: <DataProtectionCreate />,
                    },
                    {
                      path: '*',
                      element: <Navigate to={'create'} />,
                    },
                  ],
                },
              ],
            },
            {
              path: 'useCase/*',
              element: (
                <LoginAuth>
                  <AdminLayout />
                </LoginAuth>
              ),
              children: [
                {
                  path: 'knowledge/template-center',
                  element: <TemplateCenter />,
                },
                {
                  path: 'knowledge/useCase-center',
                  element: <UseCaseCenter />,
                },
              ],
            },
            {
              path: 'configure/*',
              //配置路由添加
              element: (
                <LoginAuth>
                  <AdminLayout />
                </LoginAuth>
              ),
              children: [
                {
                  path: '',
                  element: <NormalLayout />,
                  children: [
                    {
                      path: 'client/list',
                      element: <ClientList />,
                    },
                    {
                      path: 'client/download',
                      element: <ClientDownload />,
                    },
                    {
                      path: 'proxy/list',
                      element: <ProxyList />,
                    },
                    {
                      path: 'securitydomain/list',
                      element: <SecurityDomain />,
                    },
                  ],
                },
                {
                  path: '',
                  element: <NormalLayout />,
                  children: [
                    {
                      path: 'device/analytical-overview',
                      element: <AnalyticalOverview />,
                    },
                    {
                      path: 'device/link-management',
                      element: <LinkManagement />,
                    },
                    {
                      path: 'device/brand-management',
                      element: <BrandManagement />,
                    },
                    {
                      path: 'device/type-management',
                      element: <TypeManagement />,
                    },
                    {
                      path: 'device/access-management',
                      element: <AccessManagement />,
                    },
                  ],
                },
              ],
            },
            {
              path: 'overview/*',
              element: (
                <LoginAuth>
                  <AdminLayout />
                </LoginAuth>
              ),
              children: [
                {
                  path: 'dashboard',
                  element: <Dashboard />,
                },
                {
                  path: 'attck',
                  element: <Attck />,
                },
                {
                  path: 'loading',
                  element: <Loading />,
                },
                {
                  path: '*',
                  element: <Navigate to={'dashboard'} />,
                },
              ],
            },
            {
              path: 'report/*',
              element: (
                <LoginAuth>
                  <AdminLayout />
                </LoginAuth>
              ),
              children: [
                {
                  path: 'task/task-report/*',
                  element: <ReportTaskLayout />,
                  children: [
                    {
                      path: '',
                      element: <ReportTask />,
                    },
                    {
                      path: '*',
                      element: <Navigate to={'task-report'} />,
                    },
                  ],
                },
                {
                  path: 'task/general-report/*',
                  element: <NormalLayout />,
                  children: [
                    {
                      path: '',
                      element: <GeneralReport />,
                    },
                    {
                      path: '*',
                      element: <Navigate to={'general-report'} />,
                    },
                  ],
                },
              ],
            },
            {
              path: 'script/*',
              element: (
                <LoginAuth>
                  <AdminLayout />
                </LoginAuth>
              ),
              children: [
                {
                  path: 'arrange',
                  element: <Program />,
                },
                {
                  path: 'execute',
                  element: <ProgramList />,
                },
                {
                  path: 'detail',
                  element: <ScriptDetail />,
                },
                {
                  path: 'configuration',
                  element: <ScriptConfiguration />,
                },
                {
                  path: 'create',
                  element: <CreateScript />,
                },
                {
                  path: 'temporary',
                  element: <Temporary />,
                },
                {
                  path: '*',
                  element: <Navigate to={'arrange'} />,
                },
              ],
            },
            {
              path: 'configure/*',
              element: (
                <LoginAuth>
                  <AdminLayout />
                </LoginAuth>
              ),
              children: [
                {
                  path: 'site/',
                  element: <ResourceLayout />,
                  children: [
                    {
                      path: 'resource',
                      element: <Resource />,
                    },
                    {
                      path: 'collect',
                      element: <Collect />,
                    },
                  ],
                },
              ],
            },
            {
              path: 'configure/*',
              element: (
                <LoginAuth>
                  <AdminLayout />
                </LoginAuth>
              ),
              children: [
                {
                  path: 'site/',
                  element: <NormalLayout />,
                  children: [
                    {
                      path: 'asset',
                      element: <Asset />,
                    },
                    {
                      path: 'host',
                      element: <VerifyHost />,
                    },
                    {
                      path: 'mailbox',
                      element: <VerifyMailbox />,
                    },
                  ],
                },
              ],
            },
            {
              path: 'account/*',
              element: (
                <LoginAuth>
                  <AdminLayout />
                </LoginAuth>
              ),
              children: [
                {
                  path: 'center',
                  element: <Account />,
                },
                {
                  path: 'basic-info',
                  element: <BasicInfo />,
                },
                {
                  path: 'security',
                  element: <Security />,
                },
                {
                  path: 'center/add',
                  element: <AddJournaling />,
                },
              ],
            },
            {
              path: 'system/*',
              element: (
                <LoginAuth>
                  <AdminLayout />
                </LoginAuth>
              ),
              children: [
                {
                  path: '',
                  element: <NormalLayout />,
                  children: [
                    {
                      path: 'attack-management',
                      element: <AttackManagement />,
                    },
                    {
                      path: 'linkage',
                      element: <LinkageManagement />,
                    },
                    {
                      path: 'task',
                      element: <TaskManagement />,
                    },
                    {
                      path: 'authorize-manage',
                      element: <AuthorizeManage />,
                    },
                    {
                      path: 'data-outsource',
                      element: <DataOutsource />,
                    },
                    {
                      path: 'email-server',
                      element: <EmailServer />,
                    },
                    {
                      path: 'email-manage',
                      element: <EmailManage />,
                    },
                    {
                      path: 'open-api',
                      element: <OpenAPI />,
                    },
                    {
                      path: 'service-monitor',
                      element: <ServiceMonitor />,
                    },
                    {
                      path: 'operate-log',
                      element: <OperateLog />,
                    },
                    {
                      path: 'about',
                      element: <About />,
                    },
                  ],
                },
              ],
            },
            {
              path: 'operation-management/*',
              element: (
                <LoginAuth>
                  <AdminLayout />
                </LoginAuth>
              ),
              children: [
                {
                  path: 'rule-upgrade',
                  element: <RuleUpgrade />,
                },
                {
                  path: 'system-upgrade',
                  element: <SystemUpgrade />,
                },
              ],
            },
            {
              path: 'configure/*',
              element: (
                <LoginAuth>
                  <AdminLayout />
                </LoginAuth>
              ),
              children: [
                {
                  path: 'log/',
                  element: <LogLayout />,
                  children: [
                    {
                      path: 'source',
                      element: <SourceManage />,
                    },
                    {
                      path: 'parse',
                      element: <ParseRule />,
                    },
                    {
                      path: 'filter',
                      element: <Filtration />,
                    },
                    {
                      path: 'query',
                      element: <LogQuery />,
                    },
                    {
                      path: 'log-management',
                      element: <LogManagement />,
                    },
                    {
                      path: 'log-query',
                      element: <LogQuery />,
                    },
                  ],
                },
                {
                  path: 'log/gather',
                  element: <LogGather />,
                },
              ],
            },
            {
              path: '*',
              element: <Navigate to={'overview/loading'} />,
            },
          ],
        },
        {
          path: 'login',
          element: (
            <LoginAuth>
              <Login />
            </LoginAuth>
          ),
        },
        {
          path: 'maintenance',
          element: (
            <LoginAuth>
              <Maintenance />
            </LoginAuth>
          ),
        },
        {
          path: 'loading',
          element: (
            <LoginAuth>
              <Loading />
            </LoginAuth>
          ),
        },
        {
          path: 'outdated',
          element: (
            <LoginAuth>
              <Outdated />
            </LoginAuth>
          ),
        },
        {
          path: 'account/lost/password',
          element: <CommonProps />,
        },
        {
          path: '*',
          element: <Navigate to={'login'} />,
        },
      ],
    },
  ])

  return (
    <FullscreenProvider>
      <Spin
        spinning={upgradeStatus?.status === 'uploading' || (globalLoading ? globalLoading : false)}
        size="large"
        tip={
          <div className="tip">
            <span>{upgradeStatus.type === 'rule' ? '规则' : '系统'}正在升级中, 请稍后...</span>
          </div>
        }
      >
        <AppContainer>
          {open && (
            <Header status={status}>
              <div className="status-icon">
                {status === 'success' ? (
                  <IconFont type="icon-cheong2" />
                ) : status === 'uploading' ? (
                  <IconFont type="icon-shangchuanzhong" />
                ) : (
                  <IconFont type="icon-cuowu1" />
                )}
              </div>
              <div className="info">
                <div className="status">
                  <span>
                    {status === 'success'
                      ? '上传成功'
                      : status === 'uploading'
                      ? '上传中'
                      : '上传失败'}
                  </span>
                  <CloseOutlined onClick={closeCard} style={{ cursor: 'pointer', color: '#999' }} />
                </div>
                <div className="filename">
                  <Text
                    style={{ width: '300px' }}
                    ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
                  >
                    {filename}
                  </Text>
                </div>
                {status !== 'success' && (
                  <div style={{ display: 'flex' }}>
                    <Progress
                      status={
                        status === 'uploading'
                          ? 'active'
                          : status === 'success'
                          ? 'normal'
                          : 'exception'
                      }
                      percent={progress}
                      showInfo={status !== 'active' && status !== 'uploading' ? false : true}
                      style={{ marginBottom: '5px' }}
                    />
                    {status !== 'active' && status !== 'uploading' && (
                      <span style={{ color: '#F53F3F', lineHeight: '22px' }}>{progress}%</span>
                    )}
                  </div>
                )}
                {status === 'uploading' && (
                  <div className="remainingTime">
                    <span>剩余等待时间：</span>
                    <span>{remainingTime} 分钟</span>
                  </div>
                )}
              </div>
            </Header>
          )}
          {routes}
        </AppContainer>
      </Spin>
    </FullscreenProvider>
  )
}

const AppContainer = styled.div`
  height: 100vh;

  .tip {
    margin-top: 20px;
  }
`

const Header = styled.div`
  min-height: 76px;
  height: ${props =>
    props.status === 'uploading' || props.status === 'failed' || props.status === 'waiting'
      ? '122px'
      : '76px'};
  min-width: 310px;
  position: absolute;
  right: 6px;
  top: 64px;
  z-index: 1000;
  border-radius: 8px;
  border: 1px solid#c7cce9;
  outline: none;
  background-color: #fff;
  display: flex;
  justify-content: space-between;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  .status-icon {
    width: 58px;
    height: ${props =>
      props.status === 'uploading' || props.status === 'failed' || props.status === 'waiting'
        ? '122px'
        : '76px'};
    font-size: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .info {
    flex: 1;
    padding: 12px 20px 12px 0;

    .status {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
      font-weight: 500;
      color: ${props =>
        props.status === 'success'
          ? '#52c41a'
          : props.status === 'uploading'
          ? '#2973FF'
          : '#F53F3F'};
    }
  }

  .filename {
    margin-bottom: 6px;
  }

  .remainingTime {
    color: #999999;
    margin-top: 6px;
  }
`
